/**
 * Context for managing "acting as" AI agent user state.
 *
 * When a human acts as an AI agent, all API calls include the
 * X-Acting-As-AI-Agent header, and the UI shows a visual indicator.
 *
 * See docs/ai-agents.md for details.
 */
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { VirtualUser } from '../../modules/ai_agents/types';
import { clearPersistedQueryCache } from '../offline/queryPersister';

const STORAGE_KEY = 'panluma_acting_as_virtual_user';

interface ActingAsContextType {
  // Current virtual user being acted as (null if not acting as anyone)
  actingAsUser: VirtualUser | null;
  // Whether we're in acting-as mode
  isActingAs: boolean;
  // Start acting as a virtual user
  startActingAs: (virtualUser: VirtualUser) => void;
  // Stop acting as and return to normal mode
  stopActingAs: () => void;
  // Get the header value for API calls
  getActingAsHeader: () => string | null;
}

const ActingAsContext = createContext<ActingAsContextType | undefined>(undefined);

interface ActingAsProviderProps {
  children: ReactNode;
}

export function ActingAsProvider({ children }: ActingAsProviderProps) {
  const queryClient = useQueryClient();
  const [actingAsUser, setActingAsUser] = useState<VirtualUser | null>(() => {
    // Initialize from sessionStorage (per-tab)
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });

  // Persist to sessionStorage when changed
  useEffect(() => {
    if (actingAsUser) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(actingAsUser));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [actingAsUser]);

  const startActingAs = useCallback((virtualUser: VirtualUser) => {
    setActingAsUser(virtualUser);
    // Purge the shared React Query cache (in-memory + persisted IndexedDB) on
    // ENTRY too — not just on stopActingAs. Switching into the agent identity
    // changes the effective permissions + the X-Acting-As header, so any
    // human-scoped query data already cached must not render under the agent
    // context. queryClient.clear() makes mounted queries refetch immediately
    // under the new header (no reload needed, so the caller's navigation is
    // preserved); removeClient() drops the persisted snapshot so a shared/kiosk
    // browser can't retain the human's data. Mirrors stopActingAs, which
    // additionally hard-reloads on the return path. A purge failure must never
    // block entering acting-as mode.
    void clearPersistedQueryCache(queryClient).catch(() => undefined);
  }, [queryClient]);

  const stopActingAs = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    // Purge the shared React Query cache (in-memory + persisted IndexedDB) BEFORE
    // the hard reload so returning to the human context can't flash the
    // agent-scoped cached data on a shared/kiosk browser. The persisted cache is
    // one per-profile snapshot, not keyed by acting-as identity — mirrors
    // EmulationContext.endEmulation and the AuthContext auth-boundary purge.
    void clearPersistedQueryCache(queryClient)
      // A purge failure must never block returning to the human context.
      .catch(() => undefined)
      .finally(() => {
        // Reload to refresh all data for the human user
        window.location.href = '/';
      });
  }, [queryClient]);

  const getActingAsHeader = useCallback((): string | null => {
    return actingAsUser?.id ?? null;
  }, [actingAsUser]);

  const value: ActingAsContextType = {
    actingAsUser,
    isActingAs: actingAsUser !== null,
    startActingAs,
    stopActingAs,
    getActingAsHeader,
  };

  return (
    <ActingAsContext.Provider value={value}>
      {children}
    </ActingAsContext.Provider>
  );
}

export function useActingAs(): ActingAsContextType {
  const context = useContext(ActingAsContext);
  if (!context) {
    throw new Error('useActingAs must be used within an ActingAsProvider');
  }
  return context;
}

/**
 * Get the acting-as header value from sessionStorage.
 * Use this in the API client to avoid React hooks in non-component code.
 */
export function getActingAsHeaderValue(): string | null {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const user = JSON.parse(stored);
      return user?.id ?? null;
    } catch {
      return null;
    }
  }
  return null;
}
